<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import automationApprovalRiskMatrixAPI from 'dashboard/api/automationApprovalRiskMatrix';
import { useAlert } from 'dashboard/composables';
import { AUTOMATION_ACTION_TYPES } from 'dashboard/routes/dashboard/settings/automation/constants';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  BaseTable,
  BaseTableCell,
  BaseTableRow,
} from 'dashboard/components-next/table';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const RISK_LEVELS = ['low', 'medium', 'high', 'critical'];
const MIN_REASON_LENGTH = 8;

const { t } = useI18n();

const snapshot = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const feedbackState = ref('idle');
const feedbackDetail = ref('');
const backendIssues = ref([]);
const hasSubmitted = ref(false);
const savedMatrixSignature = ref('');
const form = ref({
  approvalActions: [],
  riskLevels: {},
  defaultRiskLevel: 'high',
  enforceByDefault: false,
  reason: '',
});
let activeRequestId = 0;

const snapshotActions = computed(() => snapshot.value?.actions || []);
const effectiveByAction = computed(() =>
  snapshotActions.value.reduce((payload, action) => {
    payload[action.action_name] = action;
    return payload;
  }, {})
);

function actionLabel(actionName) {
  const key = `AUTOMATION.ACTIONS.${actionName.toUpperCase()}`;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const label = t(key);
  if (label !== key) return label;

  return actionName
    .split('_')
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function riskLabel(level) {
  if (!level) return t('AUTOMATION.APPROVAL_RISK_MATRIX.RISK.NONE');

  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`AUTOMATION.APPROVAL_RISK_MATRIX.RISK.${level.toUpperCase()}`);
}

function buildMatrix() {
  const approvalActions = [...form.value.approvalActions].sort();
  const riskLevels = approvalActions.reduce((payload, actionName) => {
    payload[actionName] =
      form.value.riskLevels[actionName] ||
      effectiveByAction.value[actionName]?.risk_level ||
      form.value.defaultRiskLevel;
    return payload;
  }, {});

  return {
    approval_actions: approvalActions,
    risk_levels: riskLevels,
    default_risk_level: form.value.defaultRiskLevel,
    enforce_by_default: form.value.enforceByDefault,
  };
}

const riskOptions = computed(() =>
  RISK_LEVELS.map(level => ({
    value: level,
    label: riskLabel(level),
  }))
);

const actionOptions = computed(() => {
  const actionNames = new Set([
    ...AUTOMATION_ACTION_TYPES.map(action => action.key),
    ...snapshotActions.value.map(action => action.action_name),
  ]);

  return [...actionNames]
    .filter(Boolean)
    .map(actionName => ({
      value: actionName,
      label: actionLabel(actionName),
    }))
    .sort((first, second) => first.label.localeCompare(second.label));
});

const tableHeaders = computed(() => [
  { key: 'enabled', label: t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.ENABLED') },
  { key: 'action', label: t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.ACTION') },
  { key: 'source', label: t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.SOURCE') },
  {
    key: 'effectiveRisk',
    label: t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.EFFECTIVE_RISK'),
  },
  {
    key: 'overrideRisk',
    label: t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.OVERRIDE_RISK'),
  },
]);

const matrixRows = computed(() =>
  actionOptions.value.map(option => {
    const effective = effectiveByAction.value[option.value] || {};
    const selected = form.value.approvalActions.includes(option.value);

    return {
      actionName: option.value,
      label: option.label,
      selected,
      source: effective.source || 'not_configured',
      effectiveRisk: effective.risk_level || '',
      overrideRisk:
        form.value.riskLevels[option.value] ||
        effective.risk_level ||
        form.value.defaultRiskLevel,
    };
  })
);

const sourceCounts = computed(() => snapshot.value?.source_counts || {});
const sourceDetails = computed(() =>
  Object.entries(snapshot.value?.source_details || {}).map(
    ([source, count]) => ({
      source,
      count,
    })
  )
);

const summaryItems = computed(() => [
  {
    key: 'plan',
    label: t('AUTOMATION.APPROVAL_RISK_MATRIX.SUMMARY.PLAN'),
    value:
      snapshot.value?.plan_name || t('AUTOMATION.APPROVAL_RISK_MATRIX.EMPTY'),
  },
  {
    key: 'actions',
    label: t('AUTOMATION.APPROVAL_RISK_MATRIX.SUMMARY.ACTIONS'),
    value: snapshot.value?.action_count ?? 0,
  },
  {
    key: 'account',
    label: t('AUTOMATION.APPROVAL_RISK_MATRIX.SUMMARY.ACCOUNT_OVERRIDES'),
    value: sourceCounts.value.account || 0,
  },
  {
    key: 'enforceByDefault',
    label: t('AUTOMATION.APPROVAL_RISK_MATRIX.SUMMARY.ENFORCE_BY_DEFAULT'),
    value: snapshot.value?.enforce_by_default
      ? t('AUTOMATION.APPROVAL_RISK_MATRIX.YES')
      : t('AUTOMATION.APPROVAL_RISK_MATRIX.NO'),
  },
]);

const readOnlyFlags = computed(() => {
  const sideEffects = snapshot.value?.side_effects || {};

  return [
    {
      key: 'providerCalls',
      label: t('AUTOMATION.APPROVAL_RISK_MATRIX.READ_ONLY.PROVIDER_CALLS'),
      value: sideEffects.provider_calls === false,
    },
    {
      key: 'externalDispatch',
      label: t('AUTOMATION.APPROVAL_RISK_MATRIX.READ_ONLY.EXTERNAL_DISPATCH'),
      value: sideEffects.external_dispatch === false,
    },
    {
      key: 'databaseWrites',
      label: t('AUTOMATION.APPROVAL_RISK_MATRIX.READ_ONLY.DATABASE_WRITES'),
      value:
        sideEffects.database_writes === false || sideEffects.writes === 'none',
    },
    {
      key: 'auditWrites',
      label: t('AUTOMATION.APPROVAL_RISK_MATRIX.READ_ONLY.AUDIT_LOG_WRITES'),
      value: sideEffects.audit_log_writes === false,
    },
  ];
});

const validationIssues = computed(() => {
  if (!hasSubmitted.value) return [];

  const issues = [];
  if (form.value.reason.trim().length < MIN_REASON_LENGTH) {
    issues.push({
      path: 'reason',
      code: 'required',
      message: t('AUTOMATION.APPROVAL_RISK_MATRIX.VALIDATION.REASON'),
    });
  }

  return issues;
});

const visibleIssues = computed(() => [
  ...validationIssues.value,
  ...backendIssues.value,
]);

const matrixSignature = computed(() => JSON.stringify(buildMatrix()));
const hasWritableChanges = computed(
  () => matrixSignature.value !== savedMatrixSignature.value
);
const canSave = computed(
  () => Boolean(snapshot.value) && hasWritableChanges.value && !isLoading.value
);

const feedbackMessage = computed(() => {
  if (isLoading.value) return t('AUTOMATION.APPROVAL_RISK_MATRIX.LOADING');
  if (feedbackState.value === 'permission') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.PERMISSION');
  }
  if (feedbackState.value === 'load_error') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.LOAD_ERROR');
  }
  if (feedbackState.value === 'validation') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.VALIDATION.TITLE');
  }
  if (feedbackState.value === 'error') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.ERROR');
  }
  if (feedbackState.value === 'success') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.SUCCESS');
  }
  if (snapshot.value?.status === 'read_only') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.READ_ONLY.TITLE');
  }
  return t('AUTOMATION.APPROVAL_RISK_MATRIX.READY');
});

const feedbackToneClass = computed(() => {
  if (feedbackState.value === 'success') return 'bg-n-teal-3 text-n-teal-12';
  if (feedbackState.value === 'validation')
    return 'bg-n-amber-3 text-n-amber-12';
  if (['load_error', 'permission', 'error'].includes(feedbackState.value)) {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }
  return 'bg-n-slate-2 text-n-slate-11';
});

const feedbackRole = computed(() =>
  ['load_error', 'permission', 'validation', 'error'].includes(
    feedbackState.value
  )
    ? 'alert'
    : 'status'
);

function riskToneClass(level) {
  return (
    {
      low: 'bg-n-teal-3 text-n-teal-12',
      medium: 'bg-n-slate-2 text-n-slate-11',
      high: 'bg-n-amber-3 text-n-amber-12',
      critical: 'bg-n-ruby-3 text-n-ruby-12',
    }[level] || 'bg-n-slate-2 text-n-slate-11'
  );
}

function sourceLabel(source) {
  if (source === 'account') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.SOURCE.ACCOUNT');
  }
  if (source === 'default') {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.SOURCE.DEFAULT');
  }
  if (source?.startsWith('plan:')) {
    return t('AUTOMATION.APPROVAL_RISK_MATRIX.SOURCE.PLAN', {
      plan: source.replace('plan:', ''),
    });
  }

  return t('AUTOMATION.APPROVAL_RISK_MATRIX.SOURCE.NOT_CONFIGURED');
}

function issueLabel(issue) {
  const path = issue.path || issue.field || '-';
  const message = issue.message || issue.code || '-';
  return t('AUTOMATION.APPROVAL_RISK_MATRIX.ISSUE', { path, message });
}

function hydrateForm(nextSnapshot) {
  const accountActions = (nextSnapshot?.actions || []).filter(
    action => action.source === 'account'
  );
  const riskLevels = accountActions.reduce((payload, action) => {
    payload[action.action_name] = action.risk_level || 'high';
    return payload;
  }, {});

  form.value = {
    approvalActions: accountActions.map(action => action.action_name).sort(),
    riskLevels,
    defaultRiskLevel: 'high',
    enforceByDefault: Boolean(nextSnapshot?.enforce_by_default),
    reason: '',
  };
  hasSubmitted.value = false;
  backendIssues.value = [];
  savedMatrixSignature.value = JSON.stringify(buildMatrix());
}

function mapErrorState(error, fallbackState = 'error') {
  const status = error?.response?.status;
  const errorMessage = error?.response?.data?.error || '';
  feedbackDetail.value = errorMessage;
  backendIssues.value = error?.response?.data?.issues || [];

  if (status === 401 || status === 403) return 'permission';
  if (status === 422 || backendIssues.value.length) return 'validation';
  return fallbackState;
}

async function loadMatrix() {
  const requestId = activeRequestId + 1;
  activeRequestId = requestId;
  isLoading.value = true;
  feedbackState.value = 'idle';
  feedbackDetail.value = '';
  backendIssues.value = [];

  try {
    const response = await automationApprovalRiskMatrixAPI.get();
    if (requestId !== activeRequestId) return;

    snapshot.value = response.data.payload;
    hydrateForm(snapshot.value);
  } catch (error) {
    if (requestId !== activeRequestId) return;

    snapshot.value = null;
    feedbackState.value = mapErrorState(error, 'load_error');
  } finally {
    if (requestId === activeRequestId) {
      isLoading.value = false;
    }
  }
}

function resetForm() {
  if (!snapshot.value) return;

  hydrateForm(snapshot.value);
  feedbackState.value = 'idle';
  feedbackDetail.value = '';
}

function toggleApprovalAction(actionName, event) {
  const enabled = event.target.checked;
  if (enabled) {
    const approvalActions = [
      ...new Set([...form.value.approvalActions, actionName]),
    ].sort();
    form.value.approvalActions = approvalActions;
    form.value.riskLevels = {
      ...form.value.riskLevels,
      [actionName]:
        form.value.riskLevels[actionName] ||
        effectiveByAction.value[actionName]?.risk_level ||
        form.value.defaultRiskLevel,
    };
    return;
  }

  form.value.approvalActions = form.value.approvalActions.filter(
    selectedAction => selectedAction !== actionName
  );
  const { [actionName]: _removed, ...riskLevels } = form.value.riskLevels;
  form.value.riskLevels = riskLevels;
}

function setActionRisk(actionName, riskLevel) {
  form.value.riskLevels = {
    ...form.value.riskLevels,
    [actionName]: riskLevel,
  };
}

async function saveMatrix() {
  hasSubmitted.value = true;
  backendIssues.value = [];
  feedbackDetail.value = '';

  if (validationIssues.value.length) {
    feedbackState.value = 'validation';
    return;
  }

  if (!canSave.value || isSaving.value) return;

  isSaving.value = true;
  feedbackState.value = 'idle';

  try {
    const response = await automationApprovalRiskMatrixAPI.update({
      matrix: buildMatrix(),
      reason: form.value.reason.trim(),
    });
    const payload = response.data.payload;
    snapshot.value = payload.snapshot;
    hydrateForm(snapshot.value);
    feedbackState.value = 'success';
    useAlert(t('AUTOMATION.APPROVAL_RISK_MATRIX.SUCCESS'));
  } catch (error) {
    feedbackState.value = mapErrorState(error);
  } finally {
    isSaving.value = false;
  }
}

onMounted(loadMatrix);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AUTOMATION.APPROVAL_RISK_MATRIX.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUTOMATION.APPROVAL_RISK_MATRIX.TITLE')"
        :description="$t('AUTOMATION.APPROVAL_RISK_MATRIX.DESCRIPTION')"
        :back-button-label="$t('AUTOMATION.HEADER')"
        feature-name="automation"
      >
        <template #actions>
          <Button
            faded
            slate
            sm
            icon="i-lucide-refresh-cw"
            :label="$t('AUTOMATION.APPROVAL_RISK_MATRIX.REFRESH')"
            :is-loading="isLoading"
            data-test="risk-matrix-refresh"
            @click="loadMatrix"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        class="rounded-lg px-3 py-2 text-sm"
        :class="feedbackToneClass"
        :role="feedbackRole"
        data-test="risk-matrix-feedback"
      >
        {{ feedbackMessage }}
        <span v-if="feedbackDetail" class="block pt-1 text-xs">
          {{ feedbackDetail }}
        </span>
      </div>

      <ul
        v-if="visibleIssues.length"
        class="m-0 grid list-none gap-1 rounded-lg bg-n-amber-2 p-3 text-xs text-n-amber-12"
        data-test="risk-matrix-issues"
      >
        <li
          v-for="issue in visibleIssues"
          :key="`${issue.path || issue.field}-${issue.code}`"
        >
          {{ issueLabel(issue) }}
        </li>
      </ul>

      <section
        v-if="snapshot"
        class="rounded-lg border border-n-weak bg-n-background p-4"
        data-test="risk-matrix-summary"
      >
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div
            v-for="item in summaryItems"
            :key="item.key"
            class="rounded-lg border border-n-weak px-3 py-2"
          >
            <div class="text-xs font-medium text-n-slate-11">
              {{ item.label }}
            </div>
            <div class="mt-1 text-sm font-semibold text-n-slate-12">
              {{ item.value }}
            </div>
          </div>
        </div>

        <div class="mt-4 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
          <div class="rounded-lg border border-n-weak p-3">
            <h3 class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.READ_ONLY.CONTRACT') }}
            </h3>
            <ul class="m-0 grid list-none gap-2 p-0 text-sm text-n-slate-11">
              <li
                v-for="flag in readOnlyFlags"
                :key="flag.key"
                class="flex items-center gap-2"
              >
                <i
                  class="text-base"
                  :class="
                    flag.value
                      ? 'i-lucide-check-circle-2 text-n-teal-10'
                      : 'i-lucide-alert-triangle text-n-amber-10'
                  "
                />
                <span>{{ flag.label }}</span>
              </li>
            </ul>
          </div>

          <div class="rounded-lg border border-n-weak p-3">
            <h3 class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.SOURCE.TITLE') }}
            </h3>
            <ul class="m-0 grid list-none gap-2 p-0 text-sm text-n-slate-11">
              <li
                v-for="detail in sourceDetails"
                :key="detail.source"
                class="flex items-center justify-between gap-3"
              >
                <span>{{ sourceLabel(detail.source) }}</span>
                <span class="font-semibold text-n-slate-12">
                  {{ detail.count }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section
        v-if="snapshot"
        class="rounded-lg border border-n-weak bg-n-background p-4"
        data-test="risk-matrix-form"
      >
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end">
          <label class="flex min-w-[220px] flex-1 flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.DEFAULT_RISK') }}
            </span>
            <Select
              v-model="form.defaultRiskLevel"
              class="w-full"
              :options="riskOptions"
              data-test="risk-matrix-default-risk"
            />
          </label>

          <label
            class="flex min-w-[220px] flex-1 items-center justify-between gap-3 rounded-lg border border-n-weak px-3 py-2"
          >
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{
                  $t('AUTOMATION.APPROVAL_RISK_MATRIX.ENFORCE_BY_DEFAULT.LABEL')
                }}
              </span>
              <span class="block text-xs text-n-slate-11">
                {{
                  $t('AUTOMATION.APPROVAL_RISK_MATRIX.ENFORCE_BY_DEFAULT.HELP')
                }}
              </span>
            </span>
            <ToggleSwitch
              v-model="form.enforceByDefault"
              data-test="risk-matrix-enforce-by-default"
            />
          </label>
        </div>

        <TextArea
          v-model="form.reason"
          class="mt-4"
          :label="$t('AUTOMATION.APPROVAL_RISK_MATRIX.REASON.LABEL')"
          :placeholder="
            $t('AUTOMATION.APPROVAL_RISK_MATRIX.REASON.PLACEHOLDER')
          "
          :message="
            validationIssues.length
              ? $t('AUTOMATION.APPROVAL_RISK_MATRIX.VALIDATION.REASON')
              : ''
          "
          message-type="error"
          :rows="3"
          :max-length="280"
          show-character-count
          data-test="risk-matrix-reason"
        />

        <div class="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            faded
            slate
            sm
            icon="i-lucide-undo-2"
            :label="$t('AUTOMATION.APPROVAL_RISK_MATRIX.RESET')"
            :disabled="isSaving || !hasWritableChanges"
            data-test="risk-matrix-reset"
            @click="resetForm"
          />
          <Button
            solid
            blue
            sm
            icon="i-lucide-save"
            :label="
              isSaving
                ? $t('AUTOMATION.APPROVAL_RISK_MATRIX.SAVING')
                : $t('AUTOMATION.APPROVAL_RISK_MATRIX.SAVE')
            "
            :disabled="!canSave || isSaving"
            :is-loading="isSaving"
            data-test="risk-matrix-save"
            @click="saveMatrix"
          />
        </div>
      </section>

      <section
        v-if="snapshot"
        class="overflow-hidden rounded-lg border border-n-weak bg-n-background p-4"
        data-test="risk-matrix-table"
      >
        <div class="mb-3">
          <h3 class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.TITLE') }}
          </h3>
          <p class="m-0 text-xs text-n-slate-11">
            {{ $t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.SUBTITLE') }}
          </p>
        </div>

        <BaseTable
          :headers="tableHeaders"
          :items="matrixRows"
          row-key="actionName"
          :no-data-message="$t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.EMPTY')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="row in items"
              :key="row.actionName"
              :item="row"
              :data-test="`risk-matrix-row-${row.actionName}`"
            >
              <BaseTableCell>
                <label class="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                    :checked="row.selected"
                    :aria-label="
                      $t('AUTOMATION.APPROVAL_RISK_MATRIX.TABLE.TOGGLE', {
                        action: row.label,
                      })
                    "
                    :data-test="`risk-matrix-toggle-${row.actionName}`"
                    @change="toggleApprovalAction(row.actionName, $event)"
                  />
                </label>
              </BaseTableCell>

              <BaseTableCell>
                <div class="font-medium text-n-slate-12">
                  {{ row.label }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ row.actionName }}
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-sm text-n-slate-11">
                  {{ sourceLabel(row.source) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="inline-flex rounded-md px-2 py-1 text-xs font-medium"
                  :class="riskToneClass(row.effectiveRisk)"
                >
                  {{ riskLabel(row.effectiveRisk) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <Select
                  :model-value="row.overrideRisk"
                  :options="riskOptions"
                  :disabled="!row.selected"
                  :data-test="`risk-matrix-risk-${row.actionName}`"
                  @update:model-value="setActionRisk(row.actionName, $event)"
                />
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
      </section>
    </template>
  </SettingsLayout>
</template>
